import { useMemo, useState, useEffect } from "react";
import { UserCheck, Mail, MapPin, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import ComuneAutocomplete from "../ComuneAutocomplete";
import {
  DEFAULT_DESTINATARIO_FATTURAZIONE,
  mergeDestinatarioEffettivo,
  normalizeDestinatarioFatturazione,
  prefillDestinatarioFamiliare,
} from "../../utils/destinatarioFatturazione";

function FieldRow({ label, value, mono }) {
  return (
    <div>
      <Label className="text-slate-500 text-xs">{label}</Label>
      <p className={`font-medium text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
}

function DestinatarioFields({ value, onPatch, showNomeSplit = true }) {
  const patch = (p) => onPatch({ ...value, ...p });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {showNomeSplit ? (
        <>
          <div>
            <Label className="text-xs text-slate-500">Nome</Label>
            <Input
              value={value.nome || ""}
              onChange={(e) => patch({ nome: e.target.value })}
              className="mt-1"
              placeholder="Mario"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Cognome</Label>
            <Input
              value={value.cognome || ""}
              onChange={(e) => patch({ cognome: e.target.value })}
              className="mt-1"
              placeholder="Rossi"
            />
          </div>
        </>
      ) : (
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500">Nome e cognome / Ragione sociale</Label>
          <Input
            value={[value.nome, value.cognome].filter(Boolean).join(" ") || value.nome_completo || ""}
            onChange={(e) => {
              const parts = e.target.value.trim().split(/\s+/);
              patch({
                nome: parts[0] || "",
                cognome: parts.slice(1).join(" ") || "",
              });
            }}
            className="mt-1"
            placeholder="Mario Rossi"
          />
        </div>
      )}
      <div>
        <Label className="text-xs text-slate-500">Codice fiscale</Label>
        <Input
          value={value.codice_fiscale || ""}
          onChange={(e) => patch({ codice_fiscale: e.target.value.toUpperCase() })}
          className="mt-1 font-mono"
          placeholder="RSSMRA80A01H501U"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Partita IVA (se azienda/PA)</Label>
        <Input
          value={value.partita_iva || ""}
          onChange={(e) => patch({ partita_iva: e.target.value })}
          className="mt-1 font-mono"
          placeholder="01234567890"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Email</Label>
        <Input
          type="email"
          value={value.email || ""}
          onChange={(e) => patch({ email: e.target.value })}
          className="mt-1"
          placeholder="email@esempio.it"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Telefono</Label>
        <Input
          value={value.telefono || ""}
          onChange={(e) => patch({ telefono: e.target.value })}
          className="mt-1"
          placeholder="+39 333 1234567"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">PEC</Label>
        <Input
          value={value.pec || ""}
          onChange={(e) => patch({ pec: e.target.value })}
          className="mt-1"
          placeholder="pec@dominio.it"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Codice SDI</Label>
        <Input
          value={value.codice_sdi || ""}
          onChange={(e) => patch({ codice_sdi: e.target.value.toUpperCase() })}
          className="mt-1 font-mono"
          placeholder="0000000"
          maxLength={7}
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500">Indirizzo</Label>
        <Input
          value={value.indirizzo || ""}
          onChange={(e) => patch({ indirizzo: e.target.value })}
          className="mt-1"
          placeholder="Via Roma 1"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">CAP</Label>
        <Input
          value={value.cap || ""}
          onChange={(e) => patch({ cap: e.target.value })}
          className="mt-1 font-mono"
          placeholder="00100"
          maxLength={5}
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Comune</Label>
        <ComuneAutocomplete
          value={value.citta || ""}
          onChange={(val) => patch({ citta: val })}
          onSelect={(comune) => {
            patch({
              citta: comune.descrizione,
              provincia: comune.provincia || value.provincia || "",
            });
          }}
          className="mt-1"
          placeholder="Roma"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Provincia</Label>
        <Input
          value={value.provincia || ""}
          onChange={(e) => patch({ provincia: e.target.value.toUpperCase() })}
          className="mt-1 font-mono"
          placeholder="RM"
          maxLength={2}
        />
      </div>
    </div>
  );
}

function sameDestinatarioConfig(a, b) {
  const left = a || {};
  const right = b || {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (left[key] !== right[key]) return false;
  }
  return true;
}

export default function DestinatarioFatturazioneSection({
  isEditing,
  editSessionKey = 0,
  destinatario,
  amministrativa,
  anagrafica,
  onChange,
}) {
  const [localConfig, setLocalConfig] = useState(() =>
    normalizeDestinatarioFatturazione(destinatario)
  );

  useEffect(() => {
    const normalized = normalizeDestinatarioFatturazione(destinatario);
    setLocalConfig(prev => (sameDestinatarioConfig(prev, normalized) ? prev : normalized));
  }, [editSessionKey, isEditing, destinatario]);

  const config = isEditing ? localConfig : normalizeDestinatarioFatturazione(destinatario);
  const contatti = anagrafica?.contatti_familiari || [];
  const effettivo = useMemo(
    () => mergeDestinatarioEffettivo(config, anagrafica, amministrativa),
    [config, anagrafica, amministrativa]
  );

  const update = (next) => {
    const normalized = normalizeDestinatarioFatturazione(next);
    if (isEditing) setLocalConfig(normalized);
    onChange(normalized);
  };

  const handleAbilitato = (checked) => {
    update({ ...config, abilitato: checked === true });
  };

  const handleTipo = (tipo) => {
    if (tipo === "familiare") {
      update(prefillDestinatarioFamiliare({ ...config, tipo: "familiare" }, anagrafica, amministrativa));
    } else {
      update({ ...config, tipo: "altro" });
    }
  };

  const handleContattoChange = (idxStr) => {
    const idx = parseInt(idxStr, 10) || 0;
    update(
      prefillDestinatarioFamiliare(
        { ...config, contatto_familiare_indice: idx, tipo: "familiare" },
        anagrafica,
        amministrativa
      )
    );
  };

  return (
    <Card className="card-base mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-indigo-600" />
          Destinatario fatturazione
          {config.abilitato && (
            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
              Personalizzato
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Imposta un destinatario diverso dall&apos;ospite per le fatture elettroniche (familiare/tutore o altra persona).
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {isEditing ? (
          <>
            <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
              <Checkbox
                id="dest-fatt-abilitato"
                checked={config.abilitato}
                onCheckedChange={handleAbilitato}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="dest-fatt-abilitato" className="text-sm font-medium cursor-pointer">
                  Usa un destinatario diverso per la fatturazione
                </Label>
                <p className="text-xs text-slate-500">
                  Se disattivato, le fatture saranno intestate all&apos;ospite come da anagrafica.
                </p>
              </div>
            </div>

            {config.abilitato && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Tipo destinatario</Label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleTipo("familiare")}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                        config.tipo === "familiare"
                          ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      Familiare / tutore
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTipo("altro")}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                        config.tipo === "altro"
                          ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      Altra persona
                    </button>
                  </div>
                </div>

                {config.tipo === "familiare" && (
                  <div className="space-y-3">
                    {contatti.length > 1 ? (
                      <div>
                        <Label className="text-xs text-slate-500">Contatto familiare</Label>
                        <Select
                          value={String(config.contatto_familiare_indice ?? 0)}
                          onValueChange={handleContattoChange}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {contatti.map((c, i) => (
                              <SelectItem key={i} value={String(i)}>
                                {c.nome || `Contatto ${i + 1}`}
                                {c.relazione ? ` (${c.relazione})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : contatti.length === 0 ? (
                      <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <p>
                          Nessun contatto familiare in anagrafica. Inserisci i dati del destinatario qui sotto oppure
                          aggiungi un contatto nel tab Anagrafica.
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                        <p className="font-medium text-slate-800">{contatti[0]?.nome || "Familiare"}</p>
                        {contatti[0]?.relazione && (
                          <p className="text-xs text-slate-500 mt-0.5">{contatti[0].relazione}</p>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">
                      I campi sotto ripropongono i dati del familiare: completa quelli mancanti per la fatturazione elettronica.
                    </p>
                  </div>
                )}

                {config.tipo === "altro" && (
                  <p className="text-xs text-slate-500">
                    Inserisci tutti i dati necessari per la fatturazione elettronica del destinatario.
                  </p>
                )}

                <DestinatarioFields
                  value={config}
                  onPatch={update}
                  showNomeSplit={config.tipo === "familiare"}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm font-medium">Destinatario alternativo</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {config.abilitato
                    ? config.tipo === "familiare"
                      ? "Fatture intestate al familiare/tutore"
                      : "Fatture intestate a persona diversa"
                    : "Disattivato — fatture intestate all'ospite"}
                </p>
              </div>
              <Switch checked={config.abilitato} disabled aria-readonly />
            </div>

            {config.abilitato && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <UserCheck className="w-4 h-4 text-indigo-600" />
                  {effettivo.nome_completo || [effettivo.nome, effettivo.cognome].filter(Boolean).join(" ") || "—"}
                  {config.tipo === "familiare" && effettivo.relazione && (
                    <Badge variant="outline" className="text-[10px]">
                      {effettivo.relazione}
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Codice fiscale" value={effettivo.codice_fiscale} mono />
                  <FieldRow label="Partita IVA" value={effettivo.partita_iva} mono />
                  <FieldRow label="Email" value={effettivo.email} />
                  <FieldRow label="PEC" value={effettivo.pec} />
                  <FieldRow label="Codice SDI" value={effettivo.codice_sdi} mono />
                </div>
                {(effettivo.indirizzo || effettivo.citta) && (
                  <div className="flex items-start gap-2 text-sm text-slate-700">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <span>
                      {[effettivo.indirizzo, effettivo.cap, effettivo.citta, effettivo.provincia]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </span>
                  </div>
                )}
                {effettivo.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {effettivo.email}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export { DEFAULT_DESTINATARIO_FATTURAZIONE };
